import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { articles, categories } from "@/mocks/news";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterBar from "@/components/feature/FilterBar";
import SortTabs from "@/components/feature/SortTabs";
import NewsCard from "@/components/feature/NewsCard";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import HotRanking from "@/components/feature/HotRanking";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  applyFilter,
  applySort,
  defaultFilter,
  paginate,
  type NewsFilterState,
  type SortKey,
} from "@/utils/news";

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { categoryId = "all" } = useParams();
  const isAll = categoryId === "all";
  const category = categories.find((c) => c.id === categoryId);

  const [filter, setFilter] = useState<NewsFilterState>(defaultFilter);
  const [sort, setSort] = useState<SortKey>("time");
  const [page, setPage] = useState(1);

  const scope = useMemo(
    () => (isAll ? articles : articles.filter((a) => a.category === categoryId)),
    [isAll, categoryId],
  );

  const result = useMemo(() => {
    const filtered = applyFilter(scope, filter);
    return applySort(filtered, sort);
  }, [scope, filter, sort]);

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = paginate(result, currentPage, PAGE_SIZE);
  const hot = useMemo(() => applySort(scope, "hot").slice(0, 6), [scope]);

  const title = isAll ? "全部爆料" : `${category ? category.name : "爆料"}爆料`;
  const desc = isAll
    ? "51爆料网全部爆料频道，聚合各领域最新社会爆料与热点新闻，支持按时间、类型、地区筛选与热度排序。"
    : `51爆料网${category ? category.name : ""}频道，${category ? category.desc : ""}实时更新，多维度筛选与排序，助你快速掌握最新动态。`;

  usePageMeta({
    title: `${title} - 51爆料网`,
    description: desc,
    keywords: `${title},51爆料网,${category ? category.name : "全部"}爆料,热点资讯,实时新闻`,
  });

  const updateFilter = (next: NewsFilterState) => {
    setFilter(next);
    setPage(1);
  };

  const updateSort = (next: SortKey) => {
    setSort(next);
    setPage(1);
  };

  const reset = () => {
    setFilter(defaultFilter);
    setSort("time");
    setPage(1);
  };

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-5 md:px-6 md:py-6">
          <Breadcrumb items={isAll ? [{ label: "全部爆料" }] : [{ label: "爆料分类" }, { label: category ? category.name : "全部" }]} />
          <div className="mt-4 flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-500 text-background-50">
              <i className={`${category ? category.icon : "ri-apps-2-line"} text-2xl`} />
            </span>
            <div>
              <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">{title}</h1>
              <p className="mt-1.5 max-w-3xl text-sm leading-relaxed text-foreground-500">
                {isAll ? "聚合全站最新爆料与热点新闻，支持多维度筛选。" : category ? category.desc : "实时更新的爆料内容。"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <div className="flex flex-col gap-8 lg:flex-row">
          <div className="min-w-0 flex-1">
            <FilterBar value={filter} onChange={updateFilter} />

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-500">
                共找到 <span className="font-semibold text-primary-600">{result.length}</span> 条爆料
              </p>
              <SortTabs value={sort} onChange={updateSort} />
            </div>

            {pageItems.length > 0 ? (
              <>
                <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {pageItems.map((item) => (
                    <NewsCard key={item.id} article={item} />
                  ))}
                </div>
                <Pagination page={currentPage} totalPages={totalPages} onPageChange={setPage} />
              </>
            ) : (
              <div className="mt-5">
                <EmptyState
                  message="试试放宽时间范围、切换类型或地区，或直接浏览全部爆料。"
                  onReset={reset}
                />
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  <Link
                    to="/category/all"
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    <i className="ri-apps-2-line" />
                    浏览全部爆料
                  </Link>
                  <Link
                    to="/"
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    <i className="ri-home-4-line" />
                    返回首页
                  </Link>
                </div>
              </div>
            )}
          </div>

          <aside className="w-full shrink-0 space-y-6 lg:w-[340px]">
            <HotRanking list={hot} />
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="mb-3 font-heading text-base font-bold text-foreground-950">热门分类</h3>
              <div className="flex flex-wrap gap-2">
                {categories.map((c) => (
                  <Link
                    key={c.id}
                    to={`/category/${c.id}`}
                    className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors ${
                      c.id === categoryId
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    <i className={c.icon} />
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}